import { QBadge, QCheckbox } from '@qualio/ui-components';
import React from 'react';
import { Tag } from '../components/Tag';
import { User, UserTableData } from '../types/user.types';
import { BILLING_ACCESS } from '../constants';
import { getUserRoleLabel } from './translator';
import { EMAIL_MAX_LENGTH } from './validation/email';
import { UserV2Type } from './validation/user';
import { CanAccessBilling } from './validation/medtechPermissions';
import { Group, InviteStatusMap } from '../types';
import { MedtechRolesMap } from '../types/medtechRole.types';
import { MTBEUserType } from './validation/mtbe-user';
import { MTBEUserCompanyType } from './validation/mtbe-user-company';

/**
 * @description: Creates a Qbadge from given values
 * @param invite_status
 * @returns: React.ReactElement
 */
export const formQbadge = (invite_status: string) => {
  if (!invite_status || invite_status === InviteStatusMap.accepted) {
    return '';
  }
  return <QBadge>{invite_status}</QBadge>;
};

/**
 * @description: Renders an email with a break opportunity after the @, so that a wrapping
 * address splits into local part and domain instead of mid-token
 * @param email
 * @returns: React.ReactElement | string
 */
export const withBreakAfterAt = (email: string) => {
  const at = email.indexOf('@');
  if (at === -1) {
    return email;
  }
  return (
    <>
      {email.slice(0, at + 1)}
      <wbr />
      {email.slice(at + 1)}
    </>
  );
};

/**
 * @description: Provides a way to map data form API to data usable in the users table
 * @param data
 * @returns: UserTableData[]
 */
export const createTableData = (
  data: User[],
  actionHandler: (actionItemId: string, userRow: UserTableData) => void,
  groupFilterAction: (selectedGroup: string) => void,
  selectedUserIds: Set<number>,
  setSelectedUserIds: (selectedUserIds: Set<number>) => void,
): UserTableData[] => {
  return data.map((user) => {
    const { id, email, invite_status: inviteStatus, full_name: fullName, role, groups, access } = user;

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newSelectedUserIds = new Set([...selectedUserIds]);
      if (event.currentTarget.checked) {
        newSelectedUserIds.add(id);
      } else {
        newSelectedUserIds.delete(id);
      }
      setSelectedUserIds(newSelectedUserIds);
    };

    return {
      checkbox: (
        <QCheckbox
          data-cy={`${email}-checkbox`}
          aria-label="userlist_checkbox"
          isChecked={selectedUserIds.has(id)}
          onChange={handleCheckboxChange}
        />
      ),
      id,
      email,
      inviteStatus,
      fullName: fullName || 'N/A',
      role: getUserRoleLabel(role),
      jobTitle: user.job_title ?? '',
      department: user.department ?? '',
      location: user.location ?? '',
      groups: (
        <>
          {groups.map((group: { name: string }) => (
            <Tag key={`${email}${group.name}`} label={group.name} clickHandler={groupFilterAction} />
          ))}
        </>
      ),
      access: (
        <>
          {access.map((value: string) => (
            <Tag key={`${email}${value}`} label={value} />
          ))}
        </>
      ),
      actions: <div />,
    };
  });
};

export const isBillingUser = (user: User) => user.access.includes(BILLING_ACCESS);
export const hasBillingPermissionV2 = (user: UserV2Type) => Boolean(user.medtechPermissions[CanAccessBilling]);
export const isInvitePendingV2 = (user: UserV2Type) => user.inviteStatus === InviteStatusMap.pending;

export const isValidFullName = (fullName: string) => {
  return Boolean(fullName) && fullName.length <= EMAIL_MAX_LENGTH;
};

export const sortGroupsByName = (groupA: Group, groupB: Group) =>
  groupA.name.toLowerCase().localeCompare(groupB.name.toLowerCase());
export const sortUsersByFullName = (userA: User, userB: User) =>
  userA.full_name.toLowerCase().localeCompare(userB.full_name.toLowerCase());
const userHasInviteStatus = ({ invite_status: inviteStatus }: User, statusToCheckFor: string) => {
  return inviteStatus === statusToCheckFor;
};

export const isUserInvitePending = (user: User) => userHasInviteStatus(user, InviteStatusMap.pending);
export const isUserInviteAccepted = (user: User) => userHasInviteStatus(user, InviteStatusMap.accepted);
export const isInviteAccepted = (inviteStatus: User['invite_status']) => inviteStatus === InviteStatusMap.accepted;
export const isUserQm = (user: User) => user.role === MedtechRolesMap.quality;
export const isUserChangingSelf = (currentUserId: number, targetUserId: number) => currentUserId === targetUserId;

export const getUserCompany = (user: MTBEUserType, companyId: number): MTBEUserCompanyType | undefined =>
  user.companies.find(({ id }) => id === companyId);

export const isCompanyAdmin = (usercompany: MTBEUserCompanyType | undefined): boolean => Boolean(usercompany?.isAdmin);

/**
 * @description Our `updateUserById` response is actually an enriched MTBEUser,
 * so this method is returning if an enriched user is an admin for a specified company.
 * Eventually, we can remove this method when the old Edit User modal that users v1 of
 * `updateUserById is deprecated
 */
export const isUserCompanyAdmin = (user: MTBEUserType, companyId: number): boolean => {
  const usercompany = getUserCompany(user, companyId);
  const isAdmin = isCompanyAdmin(usercompany);

  return isAdmin;
};
